import { createTheme } from '@mui/material/styles'

// 4B, "shop-floor instrument".
//
// Light, decided by the room rather than by taste: staff stand in a bright
// showroom looking between a gold item and a screen, and a dark UI there is a
// mirror.
//
// The theme it replaces had two colours and one rule (`borderRadius: 33`).
// Everything else was decided at each call site, which is how ten dialogs ended
// up with seven Cancels, how green came to mean confirm in five places and red
// in three, and how a page title and a field label both became `variant='h6'`.
export const tokens = {
    // Never #fff. A flat white ground makes every surface on it invisible.
    ground: '#F7F7F9',
    surface: '#FDFDFE',
    // Never #000 either -- pure black on a bright screen is glare, not contrast.
    text: '#191921',
    textSecondary: '#61616E',
    // The nav band. Deliberately its own token rather than `primary`: an AppBar
    // defaults to primary.main, so the moment primary became the accent the
    // largest surface in the app turned indigo -- which is the accent being
    // decoration, the one job it is not allowed to have.
    chrome: '#191921',
    border: '#E2E2E8',
    // Indigo on purpose. It has no semantic clash with red, green or amber, it
    // holds contrast under showroom lighting, and nobody would guess it from
    // "jewelry" -- which is the point. Selection, active scope, primary action.
    // Never decoration.
    accent: '#4A3FD0',
    accentSoft: '#EDEBFB',
    danger: '#C0362C',
    // Status only, both of these. Green is not an action colour here: see
    // ActionBar, which has no way to ask for one.
    success: '#2E6B4F',
    // Solid rather than the success green at low alpha: this tints a grid row
    // that also has hover and selected states stacked on it, and alpha over alpha
    // makes "eligible and selected" a different colour from either.
    successSoft: '#E6F0EB',
    // The soft companion to warning, added for ScanVerdict's out-of-scope band. Solid
    // rather than warning at low alpha, for the same reason successSoft is solid.
    warningSoft: '#FBF1E0',
    warning: '#B4700F'
}

// No webfont. The two that were linked (Open Sans, Poppins) were render-blocking
// and referenced by nothing -- see finding 10. The system stack is already on
// the device, so it costs nothing and renders on the first paint.
const FONT_STACK = [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'system-ui',
    'sans-serif'
].join(', ')

const lightTheme = createTheme({
    palette: {
        mode: 'light',
        background: { default: tokens.ground, paper: tokens.surface },
        text: { primary: tokens.text, secondary: tokens.textSecondary },
        divider: tokens.border,
        primary: { main: tokens.accent, contrastText: '#FFFFFF' },
        // Secondary was a saturated blue that nothing used deliberately. It is a
        // neutral now, so reaching for it cannot accidentally shout.
        secondary: { main: tokens.textSecondary, contrastText: '#FFFFFF' },
        error: { main: tokens.danger, contrastText: '#FFFFFF' },
        success: { main: tokens.success, contrastText: '#FFFFFF' },
        warning: { main: tokens.warning, contrastText: '#FFFFFF' }
    },

    // 8px standard. Sheets and cards take 12px themselves, and 999px is reserved
    // for status pills -- a shape that should mean "this is a state", not "this
    // is a button". Everything was a 33px pill before, which is what made the
    // actions read as toy-like.
    shape: { borderRadius: 8 },

    typography: {
        fontFamily: FONT_STACK,
        // Base moves 14 -> 16. The old size was chosen for a desk; this screen is
        // read at arm's length, held in one hand, next to a lit display case.
        fontSize: 16,
        htmlFontSize: 16,
        // Fixed rem on a ~1.2 ratio. Every heading in the app used to be h6, so a
        // page title and a field-group label rendered at the same size and there
        // was nothing to skim by.
        h4: { fontSize: '1.5625rem', fontWeight: 600, lineHeight: 1.25 },
        h5: { fontSize: '1.5625rem', fontWeight: 600, lineHeight: 1.25 },
        h6: { fontSize: '1.3125rem', fontWeight: 600, lineHeight: 1.3 },
        subtitle1: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.4 },
        subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 },
        body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
        body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.45 },
        caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
        button: {
            fontSize: '1rem',
            fontWeight: 500,
            // Material's uppercase costs a word its shape, which is most of what
            // makes a familiar label readable at a glance -- and it is worse in
            // Portuguese, where the labels are longer to begin with.
            textTransform: 'none'
        }
    },

    components: {
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                // The floor for a thumb. ActionBar asks for this too; having it
                // here means a one-off button somewhere cannot fall below it.
                root: { minHeight: 44 }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                colorPrimary: { backgroundColor: tokens.chrome }
            }
        },
        MuiTextField: {
            defaultProps: { size: 'medium' }
        },
        MuiAlert: {
            styleOverrides: {
                root: { alignItems: 'center' }
            }
        }
    }
})

export default lightTheme
